/**
 * AnnouncementsProvider — wires PanLuma's announcement delivery surface.
 *
 * Fetches the current user's eligible announcements once per session, auto-opens
 * the modal after login when there are unseen ones, and exposes state to the
 * AppBar bell. This delivery is PanLuma-specific dogfooding of the Software
 * Factory module; other tenants consume the API directly.
 * See docs/platform/user-announcements.md.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useMyAnnouncements,
  useRecordAnnouncementView,
} from '../../hooks/useAnnouncementQueries';
import type { SfUserAnnouncementDelivery } from '../../types';
import { AnnouncementsModal } from './AnnouncementsModal';

interface UserAnnouncementsContextValue {
  /** Eligible, not-yet-dismissed announcements for the current user. */
  announcements: SfUserAnnouncementDelivery[];
  /** Count of non-dismissed announcements the user hasn't yet acknowledged. */
  unseenCount: number;
  /** Open the announcements modal from the start. */
  open: () => void;
}

const UserAnnouncementsContext = createContext<UserAnnouncementsContextValue | null>(null);

export function useUserAnnouncements(): UserAnnouncementsContextValue {
  const ctx = useContext(UserAnnouncementsContext);
  if (!ctx) {
    throw new Error('useUserAnnouncements must be used within an AnnouncementsProvider');
  }
  return ctx;
}

export function AnnouncementsProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const language = i18n.language?.split('-')[0] || 'en';

  const { data: fetched = [] } = useMyAnnouncements(language);
  const recordView = useRecordAnnouncementView();

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const autoOpenedRef = useRef(false);

  const announcements = useMemo(
    () => fetched.filter((a) => !dismissedIds.has(a.id)),
    [fetched, dismissedIds],
  );

  // Count non-dismissed announcements the user hasn't yet acknowledged.
  // "Tell me next time" leaves no view record so the count persists across sessions.
  const unseenCount = useMemo(
    () => announcements.filter((a) => !a.seen).length,
    [announcements],
  );

  const open = useCallback(() => {
    setIndex(0);
    setIsOpen(true);
  }, []);

  // Auto-open once per session when there are unacknowledged announcements.
  useEffect(() => {
    if (autoOpenedRef.current || fetched.length === 0) return;
    autoOpenedRef.current = true;
    if (fetched.some((a) => !a.seen)) {
      setIndex(0);
      setIsOpen(true);
    }
  }, [fetched]);

  const dismiss = useCallback(
    (id: string) => {
      setDismissedIds((prev) => new Set(prev).add(id));
      recordView.mutate({ id, dismissed: true });
    },
    [recordView],
  );

  const value = useMemo(
    () => ({ announcements, unseenCount, open }),
    [announcements, unseenCount, open],
  );

  return (
    <UserAnnouncementsContext.Provider value={value}>
      {children}
      <AnnouncementsModal
        open={isOpen}
        announcements={announcements}
        index={index}
        onIndexChange={setIndex}
        onDismiss={dismiss}
        onClose={() => setIsOpen(false)}
      />
    </UserAnnouncementsContext.Provider>
  );
}
